import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { Tea } from '@features/libs/tea';
import { setQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { FeatureGuide } from '../FeatureGuide';
import { Policy } from '../Policy';
import { Course } from '../Course';
import { triggerEventHandle } from '@/libs/useFeelgood';
import { useUserModel } from '@/models';
import { REGION_CONFIG } from '@/region-config';
import { usePopQuery } from '@/hooks/usePopQuery';

export const useTabs = () => {
  const history = useHistory();
  const { region } = usePopQuery();
  const { userIdStr } = useUserModel();
  const timer = useRef<number>();
  const { ref, inView: oriInView } = useInView({
    threshold: 1,
    rootMargin: '18px 0px 0px 100%',
    initialInView: true,
  });
  const [inView, setInView] = useState(oriInView);
  const { location } = history;
  const list = useMemo(
    () => ({
      course: {},
      feature: {
        key: 'feature',
        title: 'academy_homepage_feature_guide_title',
        desc: 'academy_homepage_feature_guide_text',
        Content: FeatureGuide,
        search: '',
        moduleName: 'feature_guide',
      },
      policy: {
        key: 'policy',
        title: 'academy_homepage_policy_center_title',
        desc: 'academy_homepage_policy_center_text',
        Content: Policy,
        search: '',
        moduleName: 'policy_center',
      },
    }),
    [],
  );
  const tabs = Object.keys(list);
  const notNeedCourse = !REGION_CONFIG.notNeedCourse;

  const active = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const menu = params.get('menu') || '';
    const isNewMarket = !REGION_CONFIG.notNeedCourse ? 'course' : 'feature';

    return list[menu]?.key ? menu : isNewMarket;
  }, [location.search]);

  const handleChange = (key: string) => {
    if (!Object.keys(list).includes(key)) {
      return;
    }

    const preTab = list[active];
    const newTab = list[key];

    preTab.search = history.location.search;

    setTimeout(() => {
      history.push({
        search: setQuery(newTab.search || history.location.search, 'menu', key),
      });
    }, 10);

    Tea('button_click', {
      page_name: 'seller_university_homepage',
      tab_name: 'home_config_l1_tab',
      module_name: preTab.moduleName,
      click_for: newTab.moduleName,
    });
  };

  const tabChangeHandle = key => {
    handleChange(key);
    if (userIdStr) {
      triggerEventHandle(key);
    }
  };

  useEffect(() => {
    if (region) {
      Object.values(list).forEach(item => ((item as any).search = ''));
    }
  }, [region]);

  useEffect(() => {
    if (notNeedCourse) {
      list.course = {
        key: 'course',
        title: 'academy_homepage_courses_title',
        desc: 'academy_homepage_courses_text',
        Content: Course,
        search: '',
        moduleName: 'course',
      };
    }
  }, []);

  useEffect(() => {
    Tea('page_show', {
      page_name: 'seller_university_homepage',
      module_name: list[active].moduleName,
    });
  }, [active]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setInView(oriInView);
    }, 10);
  }, [oriInView]);

  return {
    ref,
    inView,
    active,
    list,
    notNeedCourse,
    tabs,
    tabChangeHandle,
  };
};
